// Wiki-link parsing + resolution through the meta doc's path map
// (web-viewer spec §Stack: "links resolve through the meta doc's path map;
// unresolved links render inert").
//
// Resolution follows Obsidian's shortest-path semantics: a target matches a
// live meta entry by exact path, by path-with-.md, by trailing path suffix,
// or by basename (case-insensitive); ties pick the shortest path. Unresolved
// links stay plain text — truly inert, no dead <a> elements.

import type { FileEntry } from "@cairn/core";
import type { Parent, PhrasingContent, Root, Text } from "mdast";

/** A parsed `[[target#anchor|alias]]`. */
export interface WikiTarget {
  /** The file part, anchor stripped. */
  target: string;
  /** `#heading` / `#^block` part, without the leading `#`. */
  anchor?: string;
  /** Display alias after `|`. */
  alias?: string;
}

export function parseWikiTarget(raw: string): WikiTarget {
  const pipe = raw.indexOf("|");
  const alias = pipe >= 0 ? raw.slice(pipe + 1).trim() : undefined;
  const before = pipe >= 0 ? raw.slice(0, pipe) : raw;
  const hash = before.indexOf("#");
  const anchor = hash >= 0 ? before.slice(hash + 1).trim() : undefined;
  const target = (hash >= 0 ? before.slice(0, hash) : before).trim();
  const out: WikiTarget = { target };
  if (anchor !== undefined && anchor !== "") out.anchor = anchor;
  if (alias !== undefined && alias !== "") out.alias = alias;
  return out;
}

/** The final path segment (basename) of a vault-relative path — `folder/a.md`
 *  → `a.md`, a bare `a.md` unchanged. Pair with {@link displayName} to get the
 *  Obsidian-style shown title (`folder/a.md` → `displayName(fileName(p))` → `a`). */
export function fileName(path: string): string {
  const i = path.lastIndexOf("/");
  return i >= 0 ? path.slice(i + 1) : path;
}

function stripMd(name: string): string {
  return name.toLowerCase().endsWith(".md") ? name.slice(0, -3) : name;
}

/**
 * Resolve a wiki-link target against the live meta entries. Markdown targets
 * usually omit the `.md` extension; attachment targets carry theirs.
 * Returns the matching entry, shortest path first, or undefined.
 */
export function resolveWikiTarget(
  target: string,
  entries: FileEntry[],
): FileEntry | undefined {
  const t = target.trim();
  if (t === "") return undefined;
  const tLower = t.toLowerCase();
  const matches = entries.filter((e) => {
    if (e.deletedAt !== undefined) return false;
    const pLower = e.path.toLowerCase();
    // Exact path (as written, or with .md implied for notes).
    if (pLower === tLower || pLower === `${tLower}.md`) return true;
    // Path suffix: [[folder/Note]] from anywhere in the vault.
    if (pLower.endsWith(`/${tLower}`) || pLower.endsWith(`/${tLower}.md`)) return true;
    // Bare basename: [[Note]] or [[image.png]].
    const b = fileName(pLower);
    return b === tLower || stripMd(b) === tLower;
  });
  matches.sort((a, b) => a.path.length - b.path.length || (a.path < b.path ? -1 : 1));
  return matches[0];
}

/** The directory portion of a vault-relative path — `a/b/Note.md` → `a/b`, a
 *  root-level `Note.md` → `""` (the vault root). */
function dirName(path: string): string {
  const i = path.lastIndexOf("/");
  return i >= 0 ? path.slice(0, i) : "";
}

/**
 * Resolve `rel` against `baseDir` into a normalized absolute vault path.
 * Processes `.`/`..` segments; `..` at the vault root is CLAMPED (dropped),
 * so a link can never escape above the root — `dirname` never goes negative.
 */
function normalizeVaultPath(baseDir: string, rel: string): string {
  const segs = baseDir === "" ? [] : baseDir.split("/");
  for (const seg of rel.split("/")) {
    if (seg === "" || seg === ".") continue;
    if (seg === "..") {
      if (segs.length > 0) segs.pop(); // clamp at root: extra `..` are inert
      continue;
    }
    segs.push(seg);
  }
  return segs.join("/");
}

/** Match a live entry by EXACT (normalized) vault path, `.md` implied for
 *  notes — case-insensitive, consistent with {@link resolveWikiTarget}. */
function matchExactPath(absPath: string, entries: FileEntry[]): FileEntry | undefined {
  if (absPath === "") return undefined;
  const lower = absPath.toLowerCase();
  return entries.find((e) => {
    if (e.deletedAt !== undefined) return false;
    const p = e.path.toLowerCase();
    return p === lower || p === `${lower}.md`;
  });
}

/**
 * Resolve a STANDARD markdown link destination (the `<a>` renderer's relative
 * branch), Obsidian-style, relative to the SOURCE note's folder. This differs
 * from wiki-links (`[[…]]`), which stay vault-global via {@link resolveWikiTarget}.
 *
 * - Guards the percent-decode: a malformed encoding (e.g. `%E0%A4`) makes
 *   `decodeURIComponent` throw, so we catch and return `null` (inert link) —
 *   the note keeps rendering instead of the whole render aborting.
 * - A dot-relative (`./…`, `../…`) or slash-bearing destination is normalized
 *   against `dirname(sourcePath)` (clamped at the vault root) and matched by
 *   EXACT path.
 * - A bare basename (no slash, no dots) is matched in the source folder FIRST
 *   (Obsidian standard-link semantics — disambiguates duplicate basenames
 *   toward the same folder), then falls back to the vault-global
 *   basename/suffix resolution.
 *
 * The `#fragment` is stripped before resolution; the caller re-derives it to
 * carry onto the route. Returns the matching entry, or `null` when unresolved.
 */
export function resolveRelativeLink(
  href: string,
  sourcePath: string | undefined,
  entries: FileEntry[],
): FileEntry | null {
  const hashIdx = href.indexOf("#");
  const rawPath = hashIdx >= 0 ? href.slice(0, hashIdx) : href;
  let dest: string;
  try {
    dest = decodeURIComponent(rawPath);
  } catch {
    return null; // malformed %-encoding → inert, never throw
  }
  dest = dest.trim();
  if (dest === "") return null;

  const baseDir = dirName(sourcePath ?? "");
  const dotRelative =
    dest === "." || dest === ".." || dest.startsWith("./") || dest.startsWith("../");

  if (dotRelative || dest.includes("/")) {
    return matchExactPath(normalizeVaultPath(baseDir, dest), entries) ?? null;
  }
  // Bare basename: prefer a sibling in the source folder, then fall back to
  // the vault-global resolver (basename/suffix, shortest-path).
  const sibling = matchExactPath(normalizeVaultPath(baseDir, dest), entries);
  if (sibling) return sibling;
  return resolveWikiTarget(dest, entries) ?? null;
}

/** Display text for a link with no alias: the target as written. */
export function wikiDisplay(t: WikiTarget): string {
  if (t.alias !== undefined) return t.alias;
  return t.anchor !== undefined ? `${t.target}#${t.anchor}` : t.target;
}

/** Encode a vault-relative path for use inside a route. */
export function encodePath(path: string): string {
  return path.split("/").map(encodeURIComponent).join("/");
}

/** App route for a file inside a vault. */
export function fileRoute(vaultId: string, path: string): string {
  return `/v/${vaultId}/${encodePath(path)}`;
}

/** Display form of a FILE name: strip a single trailing `.md`, the way Obsidian
 *  hides the default extension while every other type keeps its (meaningful)
 *  extension — `foo.md` → `foo`, `a.md.md` → `a.md`, `image.png` unchanged,
 *  `.md` (a file literally so named) left intact. Display-ONLY: the real path,
 *  ids, links, and wiki resolution all still use the true `name.md`. Apply this
 *  only to file leaves — never to folder names. */
export function displayName(name: string): string {
  return name.length > 3 && name.endsWith(".md") ? name.slice(0, -3) : name;
}

/** Internal URL scheme carrying an attachment reference through mdast/hast.
 *  The Markdown component's <img>/<a> overrides swap it for a presigned GET. */
export const ATTACHMENT_SCHEME = "cairn-attachment:";

export function attachmentRef(hash: string, name: string): string {
  return `${ATTACHMENT_SCHEME}${hash}?name=${encodeURIComponent(name)}`;
}

export function parseAttachmentRef(url: string): { hash: string; name: string } | null {
  if (!url.startsWith(ATTACHMENT_SCHEME)) return null;
  const rest = url.slice(ATTACHMENT_SCHEME.length);
  const q = rest.indexOf("?name=");
  if (q < 0) return { hash: rest, name: "" };
  return { hash: rest.slice(0, q), name: decodeURIComponent(rest.slice(q + 6)) };
}

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|svg|avif|bmp|ico)$/i;

export function isImagePath(path: string): boolean {
  return IMAGE_EXT.test(path);
}

/** What a resolved embed/link turns into. */
export type WikiResolution =
  | { kind: "note"; entry: FileEntry }
  | { kind: "attachment"; entry: FileEntry; image: boolean };

export function classify(entry: FileEntry): WikiResolution {
  if (entry.kind === "md") return { kind: "note", entry };
  return { kind: "attachment", entry, image: isImagePath(entry.path) };
}

// --- remark plugin -----------------------------------------------------------

const WIKI_RE = /(!?)\[\[([^[\]\n]+)\]\]/g;

/** Node types whose text must not be rewritten. */
const SKIP = new Set(["code", "inlineCode", "link", "linkReference"]);

export interface WikiLinkOptions {
  vaultId: string;
  /** Live meta entries (MetaView.active()). */
  entries: FileEntry[];
}

function nodesForMatch(
  embed: boolean,
  inner: string,
  raw: string,
  opts: WikiLinkOptions,
): PhrasingContent[] {
  const parsed = parseWikiTarget(inner);
  const entry = resolveWikiTarget(parsed.target, opts.entries);
  if (!entry) return [{ type: "text", value: raw }]; // unresolved → inert text
  const res = classify(entry);
  const label = wikiDisplay(parsed);

  if (embed && res.kind === "attachment" && res.image) {
    return [
      {
        type: "image",
        url: attachmentRef(entry.hash ?? "", entry.path),
        alt: parsed.alias ?? entry.path,
      },
    ];
  }
  if (res.kind === "attachment") {
    // Non-image attachment (embedded or linked) → download link.
    return [
      {
        type: "link",
        url: attachmentRef(entry.hash ?? "", entry.path),
        children: [{ type: "text", value: label }],
        data: { hProperties: { className: ["wiki-link", "wiki-attachment"] } },
      },
    ];
  }
  // Note link (embeds of notes render as links — no transclusion in v1).
  return [
    {
      type: "link",
      url: fileRoute(opts.vaultId, entry.path),
      children: [{ type: "text", value: label }],
      data: { hProperties: { className: ["wiki-link"] } },
    },
  ];
}

function transformText(node: Text, opts: WikiLinkOptions): PhrasingContent[] | null {
  WIKI_RE.lastIndex = 0;
  if (!WIKI_RE.test(node.value)) return null;
  WIKI_RE.lastIndex = 0;
  const out: PhrasingContent[] = [];
  let last = 0;
  for (let m = WIKI_RE.exec(node.value); m !== null; m = WIKI_RE.exec(node.value)) {
    if (m.index > last) out.push({ type: "text", value: node.value.slice(last, m.index) });
    out.push(...nodesForMatch(m[1] === "!", m[2] ?? "", m[0], opts));
    last = m.index + m[0].length;
  }
  if (last < node.value.length) out.push({ type: "text", value: node.value.slice(last) });
  return out;
}

function walk(node: Parent, opts: WikiLinkOptions): void {
  for (let i = 0; i < node.children.length; i++) {
    const child = node.children[i];
    if (child === undefined || SKIP.has(child.type)) continue;
    if (child.type === "text") {
      const replaced = transformText(child, opts);
      if (replaced) {
        node.children.splice(i, 1, ...(replaced as typeof node.children));
        i += replaced.length - 1;
      }
    } else if ("children" in child) {
      walk(child as Parent, opts);
    }
  }
}

/** remark plugin: rewrite `[[wiki links]]` and `![[embeds]]` into mdast
 *  link/image nodes resolved through the meta path map. */
export function remarkWikiLinks(opts: WikiLinkOptions) {
  return (tree: Root): void => {
    walk(tree, opts);
  };
}
